import { X, Instagram, Facebook, ChevronRight, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import logoWhite from "@/assets/logo-white.svg";
import { PHOTOS } from "@/lib/photos";

const MENU_BG_URL = PHOTOS.CROWD.sunsetStageView;

interface NavChild {
  label: string;
  href: string;
}

interface NavLink {
  num: string;
  label: string;
  href: string;
  comingSoon: boolean;
  children?: NavChild[];
}

const navLinks: NavLink[] = [
  { num: "—", label: "Home", href: "/", comingSoon: false },
  { num: "I", label: "Tickets", href: "/tickets", comingSoon: false },
  { num: "II", label: "Lineup", href: "/lineup", comingSoon: false },
  {
    num: "III", label: "Guide", href: "/guide", comingSoon: false,
    children: [
      { label: "Schedule", href: "/schedule" },
      { label: "Parking", href: "/parking" },
      { label: "Travel", href: "/travel" },
      { label: "What to Wear", href: "/lifestyle/blog/what-to-wear-to-a-yacht-rock-festival" },
      { label: "Boat Slips (Sold Out)", href: "/guide#bring-your-boat" },
      { label: "Lifestyle", href: "/lifestyle" },
    ],
  },
];

interface FullPageMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onJoinWaitlist?: () => void;
}

const FullPageMenu = ({ isOpen, onClose, onJoinWaitlist }: FullPageMenuProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // Reset sub-menu state when menu closes
  useEffect(() => {
    if (!isOpen) {
      setHoveredItem(null);
      setMobileExpanded(null);
    }
  }, [isOpen]);

  // Handle open/close with animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isVisible) return null;

  const handleLinkClick = () => onClose();

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const isChildrenVisible = (label: string) =>
    isMobile ? mobileExpanded === label : hoveredItem === label;

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col md:flex-row overflow-hidden transition-opacity duration-300 ${
        isAnimating ? "opacity-100" : "opacity-0"
      }`}
    >
      {/* Left Side - Image with Navigation */}
      <div
        className={`flex-1 relative bg-cover bg-center transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]`}
        style={{
          backgroundImage: `url(${MENU_BG_URL})`,
          clipPath: isAnimating ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
        }}
      >
        <div className="absolute inset-0 bg-black/50" />

        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className={`absolute top-6 left-6 z-20 w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer ${
            isAnimating ? "scale-100 opacity-100" : "scale-50 opacity-0"
          }`}
          style={{ transitionDelay: isAnimating ? "400ms" : "0ms" }}
          aria-label="Close menu"
        >
          <X size={20} className={`transition-transform duration-500 ${isAnimating ? "rotate-0" : "rotate-180"}`} />
        </button>

        {/* Navigation Links */}
        <nav className="relative z-10 h-full flex flex-col justify-start md:justify-center px-6 pt-24 pb-12 md:px-20 md:pt-0 md:pb-0 overflow-y-auto md:overflow-visible overscroll-contain">
          {navLinks.map((link, index) => (
            <div
              key={link.label}
              onMouseEnter={() => !isMobile && link.children && setHoveredItem(link.label)}
              onMouseLeave={() => !isMobile && setHoveredItem(null)}
            >
              {/* Main link row */}
              <div className="flex items-center">
                <a
                  href={link.comingSoon ? undefined : link.href}
                  onClick={link.comingSoon ? (e) => e.preventDefault() : handleLinkClick}
                  className={`group flex items-center gap-4 md:gap-6 py-3 md:py-4 transition-all duration-500 ease-out ${
                    isAnimating ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                  } ${link.comingSoon ? "cursor-not-allowed" : ""}`}
                  style={{ transitionDelay: isAnimating ? `${300 + index * 100}ms` : "0ms" }}
                >
                  <span className={`text-xs md:text-sm font-light w-6 md:w-8 ${link.comingSoon ? "text-white/20" : "text-white/40"}`}>
                    {link.num}
                  </span>
                  <span
                    className={`text-[1.75rem] leading-tight md:text-5xl font-semibold tracking-wide transition-colors ${
                      link.comingSoon ? "text-white/30" : "text-white group-hover:text-yacht-yellow"
                    }`}
                  >
                    {link.label}
                  </span>
                  {link.comingSoon && (
                    <span className="text-xs uppercase tracking-wider text-white/30 font-medium">Coming Soon</span>
                  )}
                </a>

                {/* Chevron toggle for items with children */}
                {link.children && (
                  <button
                    type="button"
                    onClick={() => {
                      if (isMobile) {
                        setMobileExpanded(mobileExpanded === link.label ? null : link.label);
                      }
                    }}
                    className={`ml-1 md:ml-2 p-3 -my-1 text-white/50 hover:text-yacht-yellow transition-all duration-300 cursor-pointer ${
                      isAnimating ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                    }`}
                    style={{ transitionDelay: isAnimating ? `${300 + index * 100}ms` : "0ms" }}
                    aria-label={`Expand ${link.label} sub-menu`}
                  >
                    {isMobile ? (
                      <ChevronDown
                        size={24}
                        className={`transition-transform duration-200 ${isChildrenVisible(link.label) ? "rotate-180" : ""}`}
                      />
                    ) : (
                      <ChevronRight
                        size={24}
                        className={`transition-transform duration-200 ${isChildrenVisible(link.label) ? "rotate-90" : ""}`}
                      />
                    )}
                  </button>
                )}
              </div>

              {/* Sub-items (tree branch) */}
              {link.children && (
                <div
                  className={`overflow-hidden transition-all duration-300 ease-out ${
                    isChildrenVisible(link.label) ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  {link.children.map((child, childIdx) => (
                    <a
                      key={child.label}
                      href={child.href}
                      onClick={handleLinkClick}
                      className="group flex items-center gap-3 md:gap-4 py-2.5 md:py-2 pl-10 md:pl-24 transition-all duration-200"
                      style={{
                        transitionDelay: isChildrenVisible(link.label) ? `${childIdx * 50}ms` : "0ms",
                      }}
                    >
                      <span className="w-5 md:w-6 h-px bg-white/30 group-hover:bg-yacht-yellow transition-colors" />
                      <span className="text-lg md:text-2xl font-medium text-white/70 group-hover:text-yacht-yellow transition-colors tracking-wide">
                        {child.label}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Get Tickets CTA */}
          <button
            onClick={() => {
              onClose();
              setTimeout(() => window.location.href = '/tickets', 300);
            }}
            className={`mt-6 md:mt-8 ml-10 md:ml-14 btn-primary px-8 py-3 text-base w-fit shrink-0 transition-all duration-500 ease-out ${
              isAnimating ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: isAnimating ? "700ms" : "0ms" }}
          >
            Get Saturday Tickets
          </button>
        </nav>
      </div>

      {/* Right / Bottom Panel - Blue with Info */}
      <div
        className={`flex md:w-1/3 bg-yacht-blue flex-col md:flex-col justify-between p-6 md:p-12 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] relative overflow-hidden`}
        style={{
          clipPath: isAnimating ? "inset(0 0 0 0)" : isMobile ? "inset(100% 0 0 0)" : "inset(0 0 0 100%)",
          transitionDelay: isAnimating ? "100ms" : "0ms",
        }}
      >
        {/* Noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Desktop: full layout. Mobile: compact strip */}
        {isMobile ? (
          /* Mobile bottom strip */
          <div className="relative z-10 flex items-center justify-between">
            <a
              href="/contact"
              onClick={handleLinkClick}
              className="text-yacht-yellow font-semibold text-base tracking-wide hover:text-white transition-colors"
            >
              Contact Us
            </a>
            <div className="flex items-center gap-5">
              <a
                href="https://www.instagram.com/yachtrockdetroit/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-yacht-yellow/60 hover:text-yacht-yellow transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/p/Yacht-Rock-Detroit-61574424432230/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-yacht-yellow/60 hover:text-yacht-yellow transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            </div>
          </div>
        ) : (
          /* Desktop full panel */
          <>
            {/* Logo */}
            <div
              className={`relative z-10 flex justify-center transition-all duration-600 ease-out ${
                isAnimating ? "translate-y-0 opacity-100 scale-100" : "-translate-y-8 opacity-0 scale-90"
              }`}
              style={{ transitionDelay: isAnimating ? "400ms" : "0ms" }}
            >
              <img src={logoWhite} alt="Yacht Rock Detroit" className="h-24 w-auto opacity-90" />
            </div>

            {/* Contact Info + Contact Us */}
            <div
              className={`relative z-10 text-center transition-all duration-500 ease-out ${
                isAnimating ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
              }`}
              style={{ transitionDelay: isAnimating ? "500ms" : "0ms" }}
            >
              <h3 className="text-xl font-semibold mb-4 text-yacht-yellow">Yacht Rock Detroit</h3>
              <p className="text-yacht-yellow/70 text-sm mb-1">July 17-18, 2026</p>
              <p className="text-yacht-yellow/70 text-sm mb-6">Detroit, Michigan</p>

              <a
                href="/contact"
                onClick={handleLinkClick}
                className="inline-block border border-yacht-yellow/50 text-yacht-yellow px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide hover:bg-yacht-yellow hover:text-yacht-blue transition-all duration-300"
              >
                Contact Us
              </a>
            </div>

            {/* Social Links */}
            <div
              className={`relative z-10 flex items-center justify-center gap-6 transition-all duration-500 ease-out ${
                isAnimating ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
              }`}
              style={{ transitionDelay: isAnimating ? "600ms" : "0ms" }}
            >
              <a
                href="https://www.instagram.com/yachtrockdetroit/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-yacht-yellow/60 hover:text-yacht-yellow transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/p/Yacht-Rock-Detroit-61574424432230/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-yacht-yellow/60 hover:text-yacht-yellow transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FullPageMenu;
